import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { uploadProgressHub } from "../services/UploadProgressHub";
import { authService } from "../services/AuthService";
import { ApiClient } from "../network/ApiClient";
import { ApiEndpoints } from "../network/ApiEndpoints";

export type UploadItem = {
  fileOperationId: string;
  fileName: string;
  percent: number;
  stage: string;
  projectId?: number;
  projectRoute?: string;
  projectName?: string;
};

export type CompletedNotification = {
  id: string;
  fileNames: string[];
  timestamp: Date;
};

// Per-upload version metadata. Persisted alongside the upload so the
// minimize/restore flow can tell a new-version upload from a new-document one
// (a new version must not spawn a project-document placeholder card).
export type FileVersionInfo = {
  isNewVersion: boolean;
  rootDocumentId?: number;
};

type UploadNotificationContextType = {
  uploads: UploadItem[];
  setUploads: (uploads: UploadItem[]) => void;
  isMinimized: boolean;
  setIsMinimized: (v: boolean) => void;
  completedNotifications: CompletedNotification[];
  addCompletedNotification: (fileNames: string[]) => void;
  clearNotification: (id: string) => void;
  uploadRoute: string;
  setUploadRoute: (route: string) => void;
  operationIds: string[];
  setOperationIds: (ids: string[]) => void;
  fileNameMap: Record<string, string>;
  setFileNameMap: (map: Record<string, string>) => void;
  fileVersionMap: Record<string, FileVersionInfo>;
  setFileVersionMap: (map: Record<string, FileVersionInfo>) => void;
  recoveredUploads: UploadItem[];
  setRecoveredUploads: React.Dispatch<React.SetStateAction<UploadItem[]>>;
};

const UploadNotificationContext = createContext<UploadNotificationContextType | null>(null);

const isCompletedStage = (stage: string) => {
  const v = (stage || "").toLowerCase().trim();
  return v.includes("complete") || v.includes("success") || v === "done";
};

export const UploadNotificationProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [isMinimized, setIsMinimized] = useState(false);
  const [completedNotifications, setCompletedNotifications] = useState<CompletedNotification[]>([]);
  const [uploadRoute, setUploadRoute] = useState("");
  const [operationIds, setOperationIds] = useState<string[]>([]);
  const [fileNameMap, setFileNameMap] = useState<Record<string, string>>({});
  const [fileVersionMap, setFileVersionMap] = useState<Record<string, FileVersionInfo>>({});
  const [recoveredUploads, setRecoveredUploads] = useState<UploadItem[]>([]);

  const recoveredQueueRef = useRef<Record<string, Array<{ percent: number; stage: string }>>>({});
  const recoveredAnimTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ownedIdsRef = useRef(new Set<string>());

  const addCompletedNotification = (fileNames: string[]) => {
    setCompletedNotifications((prev) => [
      ...prev,
      { id: crypto.randomUUID(), fileNames, timestamp: new Date() },
    ]);
  };

  const clearNotification = (id: string) => {
    setCompletedNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  useEffect(() => {
    ownedIdsRef.current.clear();
    recoveredUploads.forEach((u) => ownedIdsRef.current.add(u.fileOperationId));
  }, [recoveredUploads]);

  // Subscribe to SignalR on mount for any recovered uploads
  useEffect(() => {
    const drain = () => {
      recoveredAnimTimerRef.current = null;
      let hasMore = false;
      setRecoveredUploads((prev) => {
        const updated = prev.map((u) => {
          const q = recoveredQueueRef.current[u.fileOperationId];
          if (!q || q.length === 0) return u;
          const next = q.shift()!;
          if (q.length > 0) hasMore = true;
          return { ...u, percent: next.percent, stage: next.stage || u.stage };
        });
        return updated;
      });
      if (hasMore) recoveredAnimTimerRef.current = setTimeout(drain, 350);
    };

    const unsubscribe = uploadProgressHub.subscribe((update) => {
      const q = recoveredQueueRef.current;
      if (!q[update.fileOperationId]) q[update.fileOperationId] = [];
      q[update.fileOperationId].push({
        percent: update.progress ?? 0,
        stage: update.status ?? "",
      });
      if (!recoveredAnimTimerRef.current) {
        recoveredAnimTimerRef.current = setTimeout(drain, 0);
      }
    }, ownedIdsRef.current);

    return () => {
      unsubscribe();
      if (recoveredAnimTimerRef.current) clearTimeout(recoveredAnimTimerRef.current);
    };
  }, []);

  // On mount: call ongoing-processes API and connect to SignalR for active uploads
  useEffect(() => {
    const recover = async () => {
      let token: string | null = null;
      try {
        token = await authService.getAccessToken();
      } catch (err) {
        console.warn("[UploadRecovery] Could not retrieve access token:", err);
        return;
      }
      if (!token) return;

      let response: any;
      try {
        response = await ApiClient.get<any>(
          ApiEndpoints.ongoingProcesses(),
          {},
          {}
        );
      } catch (err) {
        console.error("[UploadRecovery] Failed to fetch ongoing processes:", err);
        return;
      }

      let items: any[];
      try {
        items = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
          ? response.data.data
          : [];
      } catch (err) {
        console.error("[UploadRecovery] Unexpected response shape from ongoingProcesses:", response, err);
        return;
      }

      if (items.length === 0) return;

      const hasAnyOpId = items.some(
        (p: any) => !!(p.fileOperationId ?? p.FileOperationId)
      );
      if (!hasAnyOpId) {
        console.warn("[UploadRecovery] ongoingProcesses returned items but none had a fileOperationId:", items);
        return;
      }

      try {
        await uploadProgressHub.connect(process.env.REACT_APP_BASE_URL || "", token);
      } catch (err) {
        console.error("[UploadRecovery] SignalR connection failed; cannot track recovered uploads:", err);
        return;
      }

      // Try joining each group individually; only keep items with an active connection
      const successfulItems = (
        await Promise.all(
          items.map(async (p: any) => {
            const opId = p.fileOperationId ?? p.FileOperationId;
            if (!opId) return null;
            try {
              await uploadProgressHub.joinGroup(opId);
              return p;
            } catch (err) {
              console.warn(`[UploadRecovery] Failed to join SignalR group for operation ${opId}:`, err);
              return null;
            }
          })
        )
      ).filter(Boolean) as any[];

      if (successfulItems.length === 0) {
        console.warn("[UploadRecovery] No upload groups could be joined; recovered uploads will not be tracked.");
        return;
      }

      setRecoveredUploads(
        successfulItems.map((p: any) => ({
          fileOperationId: p.fileOperationId ?? p.FileOperationId,
          fileName: p.documentName ?? p.DocumentName ?? (p.fileOperationId ?? p.FileOperationId),
          percent: 0,
          stage: "uploading",
          projectId: p.projectId ?? p.ProjectId,
          projectRoute: `/home/${p.projectId ?? p.ProjectId}`,
          projectName: p.projectName ?? p.ProjectName,
        }))
      );
    };

    recover();
  }, []);

  // Detect completions in recovered uploads and push to completedNotifications
  useEffect(() => {
    const completed = recoveredUploads.filter(
      (u) => isCompletedStage(u.stage) || u.percent >= 100
    );
    if (completed.length === 0) return;

    const byProject = new Map<number, string[]>();
    completed.forEach((u) => {
      const id = u.projectId ?? 0;
      if (!byProject.has(id)) byProject.set(id, []);
      byProject.get(id)!.push(u.fileName);
    });

    byProject.forEach((fileNames) => addCompletedNotification(fileNames));
    const completedIds = completed.map((u) => u.fileOperationId);
    uploadProgressHub.leaveGroups(completedIds).then(() => uploadProgressHub.stop());
    setRecoveredUploads((prev) =>
      prev.filter((u) => !isCompletedStage(u.stage) && u.percent < 100)
    );
  }, [recoveredUploads]);

  return (
    <UploadNotificationContext.Provider
      value={{
        uploads,
        setUploads,
        isMinimized,
        setIsMinimized,
        completedNotifications,
        addCompletedNotification,
        clearNotification,
        uploadRoute,
        setUploadRoute,
        operationIds,
        setOperationIds,
        fileNameMap,
        setFileNameMap,
        fileVersionMap,
        setFileVersionMap,
        recoveredUploads,
        setRecoveredUploads,
      }}
    >
      {children}
    </UploadNotificationContext.Provider>
  );
};

export const useUploadNotification = () => {
  const ctx = useContext(UploadNotificationContext);
  if (!ctx) throw new Error("useUploadNotification must be used within UploadNotificationProvider");
  return ctx;
};
